/* eslint-disable import/prefer-default-export */
import BigNumber from 'bignumber.js'
import { isAddress } from 'ethers/lib/utils'
import erc20 from 'config/abi/erc20.json'
import nft from 'config/abi/nft.json'
import nftV2 from 'config/abi/nftV2.json'
import nftLaunchpadFactory from 'config/abi/nftLaunchpadFactory.json'
import { ZERO_ADDRESS, PINATA_BASE_URL } from 'config'
import { seriesesConfig } from 'config/constants'
import { SeriesKey } from 'config/constants/types'
import multicall, { multicallv2 } from 'utils/multicall'
import { simpleRpcProvider } from 'utils/providers'
import { getBalanceAmount } from 'utils/formatBalance'
import {
  getAddress,
  getNftLaunchpadFactoryAddress,
  getNftLaunchpadFactoryV2Address,
  getNftMultiTransferAddress,
} from './addressHelpers'

export enum ReturnStatus {
  INVALID_ADDRESS = 'invalidAddress',
  ADDRESS_NOT_EXIST = 'addressNotExist',
  NOT_OWNER = 'notOwner',
  SUCCESS = 'success',
  FAILED = 'failed',
}

interface CollectionDataReturn {
  status: ReturnStatus
  data?: any
}

export const getCollectionData = async (address: string, account: string): Promise<CollectionDataReturn> => {
  try {
    if (!isAddress(address)) {
      return {
        status: ReturnStatus.INVALID_ADDRESS,
      }
    }

    const [isExistFC, isExistV2FC, ownerFC] = await multicall(nftLaunchpadFactory, [
      {
        address: getNftLaunchpadFactoryAddress(),
        name: 'exists',
        params: [address],
      },
      {
        address: getNftLaunchpadFactoryV2Address(),
        name: 'exists',
        params: [address],
      },
      {
        address,
        name: 'owner',
      },
    ])
    if (!isExistFC[0] && !isExistV2FC[0]) {
      return {
        status: ReturnStatus.ADDRESS_NOT_EXIST,
      }
    }
    if (ownerFC[0].toLowerCase() !== account.toLowerCase()) {
      return {
        status: ReturnStatus.NOT_OWNER,
      }
    }

    const calls = [
      {
        address,
        name: 'notRevealedUri',
      },
      {
        address,
        name: 'price',
      },
      {
        address,
        name: 'maxMintPerTx',
      },
      {
        address,
        name: 'maxMintPerAddress',
      },
      {
        address,
        name: 'revealed',
      },
      {
        address,
        name: 'onlyWhitelisted',
      },
      {
        address,
        name: 'paused',
      },
    ]
    if (isExistFC[0]) {
      const [notRevealedUriFC, priceFC, maxMintPerTxFC, maxMintPerAddressFC, revealedFC, onlyWhitelistedFC, pausedFC] =
        await multicall(nft, calls)
      const ftmBalance = await simpleRpcProvider.getBalance(address)
      return {
        status: ReturnStatus.SUCCESS,
        data: {
          version: isExistFC[0] ? 'v1' : 'v2',
          notRevealedUri: notRevealedUriFC[0],
          price: getBalanceAmount(new BigNumber(priceFC)).toString(),
          maxMintPerTx: new BigNumber(maxMintPerTxFC).toString(),
          maxMintPerAddress: new BigNumber(maxMintPerAddressFC).toString(),
          ftmBalance: getBalanceAmount(new BigNumber(ftmBalance.toString())),
          revealed: revealedFC[0],
          paused: pausedFC[0],
          onlyWhitelisted: onlyWhitelistedFC[0],
        },
      }
    }

    const callsV2 = [
      {
        address,
        name: 'pausedERC20',
      },
      {
        address,
        name: 'pricetoken',
      },
      {
        address,
        name: 'token',
      },
    ]
    const [
      notRevealedUriFC,
      priceFC,
      maxMintPerTxFC,
      maxMintPerAddressFC,
      revealedFC,
      onlyWhitelistedFC,
      pausedFC,
      pausedERC20FC,
      pricetokenFC,
      tokenFC,
    ] = await multicall(nftV2, [...calls, ...callsV2])
    const ftmBalance = await simpleRpcProvider.getBalance(address)

    let token = tokenFC[0]
    let decimals = 18
    let symbol = ''
    if (token !== ZERO_ADDRESS) {
      const tokenData = await getTokenData(token)
      decimals = tokenData.data.decimals
      symbol = tokenData.data.symbol
    } else {
      token = ''
    }
    return {
      status: ReturnStatus.SUCCESS,
      data: {
        version: isExistFC[0] ? 'v1' : 'v2',
        notRevealedUri: notRevealedUriFC[0],
        price: getBalanceAmount(new BigNumber(priceFC)).toString(),
        pricetoken: getBalanceAmount(new BigNumber(pricetokenFC), decimals).toString(),
        maxMintPerTx: new BigNumber(maxMintPerTxFC).toString(),
        maxMintPerAddress: new BigNumber(maxMintPerAddressFC).toString(),
        ftmBalance: getBalanceAmount(new BigNumber(ftmBalance.toString())),
        revealed: revealedFC[0],
        paused: pausedFC[0],
        pausedERC20: pausedERC20FC[0],
        onlyWhitelisted: onlyWhitelistedFC[0],
        token,
        decimals,
        symbol,
      },
    }
  } catch (err) {
    console.error('fetch data error:', err)
    return {
      status: ReturnStatus.FAILED,
    }
  }
}

export const getJsonImageFromUri = async (uri: string, seriesKey = '') => {
  let uriTF = uri.replace('https://gateway.pinata.cloud/ipfs', PINATA_BASE_URL)
  // uriTF = uri.replace(DMD_PINATA_BASE_URL, PINATA_BASE_URL)
  if (uriTF.startsWith('ipfs')) {
    uriTF = `${PINATA_BASE_URL}/${uriTF.split('://')[1]}`
    const data = await fetch(uriTF)
    const blob = await data.blob()
    uriTF = URL.createObjectURL(blob)
  }

  const response = await fetch(uriTF)
  const jsonData = await response.json()

  let image = ''
  if (seriesKey === SeriesKey.FOUNDER || seriesKey === SeriesKey.FOUNDER_V2) {
    image = '/images/nft/series/founder-token.jpg'
  } else {
    image = jsonData.image
    image = image?.replace('https://gateway.pinata.cloud/ipfs', PINATA_BASE_URL)
    // image = image?.replace(DMD_PINATA_BASE_URL, PINATA_BASE_URL)
    if (image?.startsWith('ipfs')) {
      image = `${PINATA_BASE_URL}/${image.split('://')[1]}`
      const imageData = await fetch(image)
      const imageBlob = await imageData.blob()
      image = URL.createObjectURL(imageBlob)
    }
  }
  return [jsonData, image]
}

export const getNfts = async (address: string, account: string): Promise<CollectionDataReturn> => {
  try {
    const series = seriesesConfig.find((item) => getAddress(item.address).toLowerCase() === address.toLowerCase())

    const calls0 = [
      {
        address,
        name: 'name',
      },
      {
        address,
        name: 'balanceOf',
        params: [account],
      },
      {
        address,
        name: 'isApprovedForAll',
        params: [account, getNftMultiTransferAddress()],
      },
      {
        address,
        name: series?.preTotalSupply ? 'name' : 'totalSupply',
      },
    ]
    const [nameFC, balanceOfFC, isApprovedForAllFC, mintedFC] = await multicall(nft, calls0)
    const balanceOf = new BigNumber(balanceOfFC).toNumber()
    const minted = series?.preTotalSupply || new BigNumber(mintedFC).toNumber()

    let tokenIds = []
    if (series && series.notFromERC721Enumerable) {
      const calls2 = [...Array(series.key === SeriesKey.SLOTH_AVATAR ? minted - 1 : minted)].map((item, index) => {
        return {
          address,
          name: 'ownerOf',
          params: [series.key === SeriesKey.CYBERSTONED ? index : index + 1],
        }
      })
      const ownersFC = await multicall(nft, calls2)
      ownersFC
        .map((ownerFC, index) => {
          return {
            tokenId: series.key === SeriesKey.CYBERSTONED ? index : index + 1,
            address: ownerFC[0],
          }
        })
        .filter((item) => item.address.toLowerCase() === account.toLowerCase())
        .forEach((item) => tokenIds.push(item.tokenId))
    } else {
      const calls1 = [...Array(balanceOf)].map((item, index) => {
        return {
          address,
          name: 'tokenOfOwnerByIndex',
          params: [account, index],
        }
      })
      const tokenIdsFC = await multicall(nft, calls1)
      tokenIds = tokenIdsFC.map((tokenIdFC) => new BigNumber(tokenIdFC).toNumber())
    }

    const tokenURIsFC = await Promise.all(
      tokenIds.map(async (tokenId) => {
        const tokenURIFC = await multicall(nft, [
          {
            address,
            name: 'tokenURI',
            params: [tokenId],
          },
        ])
        return tokenURIFC
      }),
    )
    const nfts = await Promise.all(
      tokenIds.map(async (tokenId, i) => {
        try {
          const uri = `${tokenURIsFC[i][0]}`
          const [jsonData, image] = await getJsonImageFromUri(uri)

          return {
            nid: tokenId,
            ...jsonData,
            image,
            animationUrl: jsonData.animation_url,
          }
        } catch (error) {
          console.error(error)
          return null
        }
      }),
    )
    return {
      status: ReturnStatus.SUCCESS,
      data: {
        name: nameFC[0],
        isApprovedForAll: isApprovedForAllFC[0],
        nfts,
      },
    }
  } catch (err) {
    console.error('fetch data error:', err)
    return {
      status: ReturnStatus.FAILED,
    }
  }
}

export const getTokenData = async (address: string): Promise<CollectionDataReturn> => {
  try {
    if (address === ZERO_ADDRESS) {
      return {
        status: ReturnStatus.SUCCESS,
        data: {
          decimals: 18,
          symbol: '',
        },
      }
    }
    const [decimalsFC, symbolFC] = await multicallv2(
      erc20,
      [
        {
          address,
          name: 'decimals',
        },
        {
          address,
          name: 'symbol',
        },
      ],
      { requireSuccess: false },
    )
    if (decimalsFC && symbolFC) {
      return {
        status: ReturnStatus.SUCCESS,
        data: {
          decimals: new BigNumber(decimalsFC).toNumber(),
          symbol: symbolFC[0],
        },
      }
    }
    return {
      status: ReturnStatus.FAILED,
    }
  } catch (err) {
    console.error('fetch data error:', err)
    return {
      status: ReturnStatus.FAILED,
    }
  }
}
